import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { adminApi } from '../services/api';

const STAGES = ['', 'Not Started', 'Notification Sent', 'In Progress', 'Completed', 'Failed', 'Quarantined'];

export default function AdminContacts() {
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const page = Number(params.get('page') || 1);
  const stage = params.get('stage') || '';
  const search = params.get('search') || '';

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    adminApi.contacts({ page, stage: stage || undefined, search: search || undefined })
      .then((r) => { if (!cancelled) setRows(r.contacts); })
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [page, stage, search]);

  const update = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => { if (v) next.set(k, v); else next.delete(k); });
    next.delete('page');
    setParams(next);
  };

  const exportCsv = async () => {
    try {
      const url = await adminApi.exportChaseList(stage || null);
      const a = document.createElement('a');
      a.href = url; a.download = `fair-chase-${Date.now()}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { alert(`Export failed: ${err.message}`); }
  };

  const bulkResend = async () => {
    // Scope picker (inferred from stage dropdown):
    //   - Failed             → 'failed'        (retry past dispatch failures)
    //   - Notification Sent  → 'missing_token' (recover broken-link victims —
    //                                            contacts whose Declaration_Token
    //                                            was empty when the dispatcher
    //                                            sent their email, producing a
    //                                            `?id=X&t=` magic link)
    //   - anything else      → 'stuck'         (stage Not Started)
    //
    // For stuck and missing-token recovery, we ask the operator for the
    // PDF Sent Date so the queue is narrowed to the affected batch.
    const scope = stage === 'Failed' ? 'failed'
      : stage === 'Notification Sent' ? 'missing_token'
      : 'stuck';
    let pdfSentDate = null;
    if (scope === 'stuck' || scope === 'missing_token') {
      const today = new Date().toISOString().slice(0, 10);
      const promptCopy = scope === 'missing_token'
        ? 'Filter by PDF Sent Date (YYYY-MM-DD) — only queue contacts whose Lender_Declaration_PDF_Sent_Date matches AND whose Declaration_Token is empty (broken-link victims).\n\nLeave blank to queue ALL contacts with empty tokens in the first 10000 of the pagination window.'
        : 'Filter by PDF Sent Date (YYYY-MM-DD) — only queue contacts whose Lender_Declaration_PDF_Sent_Date matches.\n\nLeave blank to queue ALL Not-Started contacts in the first 10000 of the pagination window.';
      const raw = window.prompt(promptCopy, '2026-05-26');
      if (raw === null) return; // operator cancelled
      const trimmed = String(raw).trim();
      if (trimmed) {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
          alert(`"${trimmed}" doesn't look like a YYYY-MM-DD date. Aborting.\n\nExample: ${today}`);
          return;
        }
        pdfSentDate = trimmed;
      }
    }

    const scopeLabel = {
      stuck: pdfSentDate
        ? `stuck (stage Not Started, PDF Sent Date = ${pdfSentDate})`
        : 'stuck (stage Not Started — no date filter)',
      missing_token: pdfSentDate
        ? `missing_token (empty Declaration_Token, PDF Sent Date = ${pdfSentDate})`
        : 'missing_token (empty Declaration_Token — no date filter)',
      failed: 'failed (past dispatch failures)',
      not_started: 'not_started (same as stuck for now)',
    }[scope] || scope;

    let preview;
    try {
      preview = await adminApi.bulkResend(scope, { dryRun: true, pdfSentDate });
    } catch (err) {
      alert(`Bulk resend preview failed: ${err.message}`);
      return;
    }
    if (preview.count === 0) {
      const warn = (preview.warnings && preview.warnings.length)
        ? `\n\nWarnings:\n• ${preview.warnings.join('\n• ')}`
        : '';
      alert(`No contacts match.\nScope: ${scopeLabel}${warn}`);
      return;
    }
    const warn = (preview.warnings && preview.warnings.length)
      ? `\n\nWarnings:\n• ${preview.warnings.join('\n• ')}\n`
      : '';
    const confirmed = window.confirm(
      [
        `Bulk resend ${preview.count} contact(s)`,
        `Scope: ${scopeLabel}`,
        '',
        `Sample IDs: ${preview.sample.slice(0, 5).join(', ')}${preview.sample.length > 5 ? '…' : ''}`,
        '',
        `Dispatch cron will process at ${preview.rateLimitPerMin}/min — full drain in ~${preview.estimatedDrainMinutes} minute(s).`,
        warn,
        'Continue?',
      ].join('\n')
    );
    if (!confirmed) return;
    try {
      const r = await adminApi.bulkResend(scope, { dryRun: false, confirm: true, pdfSentDate });
      const rwarn = (r.warnings && r.warnings.length)
        ? `\n\nWarnings:\n• ${r.warnings.join('\n• ')}`
        : '';
      alert(`Queued ${r.queued} contact(s).\nDrain ETA: ~${r.estimatedDrainMinutes} minute(s).${rwarn}`);
      // Trigger a list refresh — cache was invalidated server-side.
      update({});
    } catch (err) {
      alert(`Bulk resend failed: ${err.message}`);
    }
  };

  return (
    <div className="stack-lg">
      <h1 className="h1">Contacts</h1>
      <div className="card row wrap" style={{ gap: 12 }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <label className="label">Stage</label>
          <select className="input" value={stage} onChange={(e) => update({ stage: e.target.value })}>
            {STAGES.map((s) => <option key={s} value={s}>{s || 'All stages'}</option>)}
          </select>
        </div>
        <div style={{ flex: 2, minWidth: 200 }}>
          <label className="label">Search (name)</label>
          <input className="input" value={search} onChange={(e) => update({ search: e.target.value })} placeholder="At least 3 characters" />
        </div>
        <div style={{ alignSelf: 'end', display: 'flex', gap: 8 }}>
          <button className="btn secondary" onClick={bulkResend} title="Re-queue notifications for all Not Started / Failed contacts">
            Bulk resend
          </button>
          <button className="btn secondary" onClick={exportCsv}>Export chase list</button>
        </div>
      </div>

      {loading ? <div className="card"><span className="spinner" /></div> :
       error ? <div className="card"><span className="badge danger">Error</span> <p className="muted">{error}</p></div> :
       rows.length === 0 ? <div className="card muted">No contacts match.</div> :
       <div className="card" style={{ padding: 0 }}>
         <table className="table">
           <thead>
             <tr><th>Name</th><th>Email</th><th>Stage</th><th>Notification</th><th>Lenders</th><th>Dispatched</th></tr>
           </thead>
           <tbody>
             {rows.map((r) => (
               <tr key={r.id}>
                 <td><Link to={`/admin/contacts/${r.id}`}>{r.name || '(no name)'}</Link></td>
                 <td className="small muted">{r.emailMasked}</td>
                 <td><span className="badge">{r.stage}</span></td>
                 <td><span className="badge">{r.notificationStatus || '—'}</span></td>
                 <td>{r.lenderCount}</td>
                 <td className="small muted">{r.notificationDispatchedAt ? new Date(r.notificationDispatchedAt).toLocaleString() : '—'}</td>
               </tr>
             ))}
           </tbody>
         </table>
       </div>
      }
    </div>
  );
}
